/**
 * Internacionalización tipada. Las claves se validan en compilación
 * (MessageKey) y los parámetros {nombre} se sustituyen en tiempo de ejecución.
 * Para añadir un idioma: crear i18n/<código>.ts con la forma DictShape<typeof es>.
 */
import en from '../i18n/en';
import es from '../i18n/es';

export type Locale = 'es' | 'en';

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type MessageKey = Leaves<typeof es>;
type Params = Record<string, string | number>;

const DICTIONARIES: Record<Locale, unknown> = { es, en };
let locale: Locale = 'es';
const listeners = new Set<(locale: Locale) => void>();

function lookup(dict: unknown, key: string): string | undefined {
  let node: unknown = dict;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

export function t(key: MessageKey, params?: Params): string {
  const template = lookup(DICTIONARIES[locale], key) ?? lookup(es, key) ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match));
}

/** Traducción con clave dinámica (p. ej. códigos que llegan de la API). */
export function tDynamic(key: string, params?: Params, fallback?: string): string {
  const template = lookup(DICTIONARIES[locale], key) ?? lookup(es, key);
  if (template === undefined) return fallback ?? key;
  return t(key as MessageKey, params);
}

/** Texto en el idioma actual de un objeto { es, en } procedente de la base de datos. */
export function pick(value: Record<string, string> | null | undefined): string {
  if (!value) return '';
  return value[locale] ?? value.es ?? Object.values(value)[0] ?? '';
}

export function errorMessage(code: string): string {
  return tDynamic(`errors.${code}`, undefined, t('errors.default', { code }));
}

export function getLocale(): Locale {
  return locale;
}

export function setLocale(next: Locale): void {
  if (next === locale) return;
  locale = next;
  document.documentElement.lang = next;
  for (const listener of listeners) listener(next);
}

export function onLocaleChange(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
